import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { api, friendlyError } from '../lib/api-client';
import { formatDateTime } from '../i18n/formatters';
import {
  PartnerPayoutsResponse,
  formatPartnerMoney,
  payoutStatusBadgeVariant,
  payoutStatusLabel,
} from '../lib/partner-portal';
import Container from '../components/ui/Container';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import AlertBanner from '../components/ui/AlertBanner';
import EmptyState from '../components/ui/EmptyState';
import {
  PartnerHeaderMeta,
  PartnerPagination,
  portalTableCellStyle,
  portalTableHeaderStyle,
} from '../components/partner-portal/shared';

export default function PartnerPayoutsPage() {
  const { t } = useTranslation(['partnerPortal', 'common']);
  const { partnerAccess } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PartnerPayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<PartnerPayoutsResponse>(`/partner/payouts?page=${page}&pageSize=25`);
        setData(response);
      } catch (err) {
        setError(friendlyError(err, t('errors.load')));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [page, t]);

  return (
    <Container maxWidth={1320}>
      <div style={{ display: 'grid', gap: 20 }}>
        <PageHeader
          eyebrow={t('payouts.eyebrow')}
          title={t('payouts.title')}
          subtitle={partnerAccess?.partnerName}
          description={t('payouts.description')}
          meta={partnerAccess ? <PartnerHeaderMeta access={partnerAccess} /> : undefined}
        />

        {error && <AlertBanner variant="danger">{error}</AlertBanner>}

        <Card
          style={{
            border: '1px solid var(--acc-color-border)',
            background: 'var(--card-bg)',
          }}
        >
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('payouts.manualEyebrow')}
              </div>
              <div style={{ marginTop: 8, fontSize: 18, fontWeight: 780, color: 'var(--acc-color-text)', letterSpacing: '-0.02em' }}>
                {t('payouts.manualTitle')}
              </div>
              <div style={{ marginTop: 8, maxWidth: 760, fontSize: 13, lineHeight: 1.7, color: 'var(--acc-color-text-muted)' }}>
                {t('payouts.manualDescription')}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                <Badge variant={payoutStatusBadgeVariant('draft')}>
                  {payoutStatusLabel('draft', t)}
                </Badge>
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
                  {t('payouts.statusHelp.draft')}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <Badge variant={payoutStatusBadgeVariant('approved')}>
                  {payoutStatusLabel('approved', t)}
                </Badge>
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
                  {t('payouts.statusHelp.approved')}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <Badge variant={payoutStatusBadgeVariant('paid')}>
                  {payoutStatusLabel('paid', t)}
                </Badge>
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
                  {t('payouts.statusHelp.paid')}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          {loading ? (
            <div>{t('common:actions.loading')}</div>
          ) : !data || data.payouts.length === 0 ? (
            <EmptyState
              title={t('empty.payoutsTitle')}
              description={t('empty.payoutsDescription')}
              dashed
            />
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={portalTableHeaderStyle}>{t('fields.amount')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.status')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.paymentMethod')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.paymentReference')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.paidAt')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.created')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.commissionCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payouts.map((payout) => (
                    <tr key={payout.id}>
                      <td style={portalTableCellStyle}>{formatPartnerMoney(payout.currency, payout.totalAmount)}</td>
                      <td style={portalTableCellStyle}>
                        <Badge variant={payoutStatusBadgeVariant(payout.status)}>
                          {payoutStatusLabel(payout.status, t)}
                        </Badge>
                      </td>
                      <td style={portalTableCellStyle}>{payout.paymentMethod ?? t('common:labels.notRecordedYet')}</td>
                      <td style={portalTableCellStyle}>{payout.paymentReference ?? t('common:labels.notRecordedYet')}</td>
                      <td style={portalTableCellStyle}>{formatDateTime(payout.paidAt) || t('common:labels.notAvailable')}</td>
                      <td style={portalTableCellStyle}>{formatDateTime(payout.createdAt) || t('common:labels.notAvailable')}</td>
                      <td style={portalTableCellStyle}>{payout.commissionCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <PartnerPagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      </div>
    </Container>
  );
}
